import { useEffect, useMemo, useRef, useState } from 'react'
import { theme } from '../theme'
import { Ic } from '../icons'
import { api } from '../api'
import { useSearch, useNavigate } from '../router'
import { fmtCanon } from '../units'
import type { IngredientRow, PantryItem, Shortage, ShoppingItem, Me } from '../types'
import { splitTerms, matchesTerms, Highlight } from '../search'
import { IngredientEditModal } from './IngredientEditModal'
import { PantryItemEditModal } from './PantryItemEditModal'
import { ShoppingItemModal } from './ShoppingItemModal'
import { PantryTableView } from './PantryTableView'

type ShoppingSeed = {
  ingredient: IngredientRow
  quantity?: number
  unit?: string
}

export function PantryTab({ mobile, me }: { mobile: boolean; me: Me }) {
  const unitPrefs = { mass: me.mass_units, volume: me.volume_units }
  const [ingredients, setIngredients] = useState<IngredientRow[]>([])
  const [pantryItems, setPantryItems] = useState<PantryItem[]>([])
  const [shortages, setShortages] = useState<Shortage[]>([])
  const [shoppingItems, setShoppingItems] = useState<ShoppingItem[]>([])
  const [editingShop, setEditingShop] = useState<ShoppingItem | null>(null)
  const [addingShop, setAddingShop] = useState(false)
  const [toast, setToast] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [editingIng, setEditingIng] = useState<IngredientRow | null>(null)
  const [editingLot, setEditingLot] = useState<PantryItem | null>(null)
  const [newLotSeed, setNewLotSeed] = useState<ShoppingSeed | null>(null)
  const [showAllKnown, setShowAllKnown] = useState(false)
  const [filterQuery, setFilterQuery] = useState('')
  const fq = filterQuery.trim().toLowerCase()
  // Split into terms so the filter matches each word independently as a
  // substring — same AND-of-LIKE semantics as the ingredient picker's backend
  // search ("frozen spinach" matches "frozen chopped spinach"), not a single
  // contiguous-substring match.
  const fterms = useMemo(() => splitTerms(filterQuery), [filterQuery])

  const [viewMode, setViewMode] = useState<'list' | 'table'>('table')
  const setAndPersistViewMode = setViewMode

  // The focused sub-tab lives in the URL (`/pantry?tab=stock|shopping`) so the
  // agent can deep-link to either via `navigate`. URL is the single source of
  // truth; absence of the param means "stock". Toggling replaces history so
  // sub-tab flips don't pile up on the back stack.
  const navigate = useNavigate()
  const search = useSearch()
  const subTab: 'stock' | 'shopping' =
    new URLSearchParams(search).get('tab') === 'shopping' ? 'shopping' : 'stock'
  const setSubTab = (v: 'stock' | 'shopping') => navigate(`/pantry?tab=${v}`, { replace: true })

  // Modal `key` is used to force a remount with fresh useState when a
  // different item is opened. Track the last *opened* key so closing
  // doesn't flip the key (which would unmount mid-fade and skip the close
  // transition). The key only changes when a new item is opened.
  const [ingModalKey, setIngModalKey] = useState<string>('init')
  useEffect(() => {
    if (editingIng) setIngModalKey(String(editingIng.id))
  }, [editingIng])
  const [lotModalKey, setLotModalKey] = useState<string>('init')
  // Bump a per-open sequence so two consecutive "Add lot" opens (which would
  // otherwise produce the same key, e.g. `n-x` for an empty seed) force a
  // remount and reset the picker. Without this the second open inherits the
  // previously-picked ingredient.
  const lotOpenSeq = useRef(0)
  useEffect(() => {
    if (editingLot) setLotModalKey(`e-${editingLot.id}-${++lotOpenSeq.current}`)
    else if (newLotSeed) setLotModalKey(`n-${newLotSeed.ingredient?.id ?? 'x'}-${++lotOpenSeq.current}`)
  }, [editingLot, newLotSeed])

  const refreshShopping = () => {
    api.shopping().then(p => {
      setShortages(p.suggestions)
      setShoppingItems(p.items)
    }).catch(() => {})
  }

  const shopModalKey = editingShop ? `e-${editingShop.id}` : addingShop ? 'new' : 'init'

  // Promote a suggestion onto the explicit shopping list. Uses
  // suggested_packs × purchase_qty / purchase_unit when available; falls back
  // to the canonical shortfall if the ingredient has no purchase pack set.
  const promoteSuggestion = async (s: Shortage) => {
    const qty = s.purchase_qty && s.suggested_packs
      ? s.suggested_packs * s.purchase_qty
      : s.shortfall_canonical
    const unit = s.purchase_unit || canonicalUnit(s.category)
    try {
      await api.createShoppingItem({
        ingredient_id: s.ingredient_id, quantity: qty, unit,
      })
    } catch (e) {
      setError(String(e))
    }
  }

  // Open the edit modal on a user-owned ingredient. If the row is from the
  // system catalog, fork it first so the modal has something editable. The
  // fork is idempotent — if the user already has a copy we get back its id.
  const openEdit = async (ing: IngredientRow) => {
    if (ing.owned) { setEditingIng(ing); return }
    try {
      const userRow = await api.adoptIngredient(ing.id)
      setEditingIng(userRow)
    } catch (e) {
      setError(String(e))
    }
  }

  // "Add to pantry" from a shortage row. Adopt catalog ingredients first so the
  // backend can backfill purchase_qty / shelf_life onto the user-owned copy.
  const openAddToPantry = async (ing: IngredientRow, s: Shortage) => {
    let userIng = ing
    if (!ing.owned) {
      try {
        userIng = await api.adoptIngredient(ing.id)
      } catch (e) {
        setError(String(e))
        return
      }
    }
    const qty = s.purchase_qty ? (s.suggested_packs || 1) * s.purchase_qty : undefined
    setNewLotSeed({
      ingredient: userIng,
      quantity: qty,
      unit: s.purchase_unit,
    })
  }

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    Promise.all([
      api.ingredients('', 200),
      api.pantryItems(),
      api.shopping(),
    ]).then(([is, ps, sh]) => {
      if (cancelled) return
      setIngredients([...is.owned, ...is.catalog]); setPantryItems(ps)
      setShortages(sh.suggestions); setShoppingItems(sh.items)
    }).catch(e => { if (!cancelled) setError(String(e)) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [])

  // SSE patch handling.
  useEffect(() => {
    const onPatch = (e: Event) => {
      const p = (e as CustomEvent).detail as
        | { op: 'upsert'; table: 'ingredient'; row: IngredientRow }
        | { op: 'upsert'; table: 'pantry_item'; row: PantryItem }
        | { op: 'upsert'; table: 'shopping_item'; row: ShoppingItem }
        | { op: 'delete'; table: 'ingredient' | 'pantry_item' | 'shopping_item'; id: number; note?: string }
      if (!p) return
      if (p.table === 'ingredient') {
        if (p.op === 'upsert') {
          setIngredients(arr => {
            const i = arr.findIndex(x => x.id === p.row.id)
            if (i < 0) return [...arr, p.row].sort((a, b) => a.name.localeCompare(b.name))
            const next = arr.slice(); next[i] = p.row; return next
          })
        } else {
          setIngredients(arr => arr.filter(x => x.id !== p.id))
        }
        refreshShopping()
      } else if (p.table === 'pantry_item') {
        if (p.op === 'upsert') {
          setPantryItems(arr => {
            const i = arr.findIndex(x => x.id === p.row.id)
            if (i < 0) return [...arr, p.row]
            const next = arr.slice(); next[i] = p.row; return next
          })
        } else {
          setPantryItems(arr => arr.filter(x => x.id !== p.id))
        }
        refreshShopping()
      } else if (p.table === 'shopping_item') {
        if (p.op === 'upsert') {
          setShoppingItems(arr => {
            const i = arr.findIndex(x => x.id === p.row.id)
            if (i < 0) return [...arr, p.row].sort((a, b) => a.ingredient_name.localeCompare(b.ingredient_name))
            const next = arr.slice(); next[i] = p.row; return next
          })
        } else {
          setShoppingItems(arr => arr.filter(x => x.id !== p.id))
          if (p.note) setToast(p.note)
        }
        refreshShopping()
      }
    }
    window.addEventListener('mila:patch', onPatch)
    return () => window.removeEventListener('mila:patch', onPatch)
  }, [])

  // Auto-dismiss toast after a few seconds.
  useEffect(() => {
    if (!toast) return
    const t = window.setTimeout(() => setToast(null), 5000)
    return () => window.clearTimeout(t)
  }, [toast])

  // Refresh shortages when a meal patch lands (status / recipe assignment
  // changes affect demand). App.tsx already handles plan state; we just hook
  // the same channel here to recompute the suggestion list.
  useEffect(() => {
    const onRefresh = () => refreshShopping()
    window.addEventListener('mila:meals-changed', onRefresh)
    return () => window.removeEventListener('mila:meals-changed', onRefresh)
  }, [])

  // Build the lookup from shortages first so we don't depend on the (capped,
  // paginated) ingredients catalog being loaded — shortage rows carry enough
  // (id, name, owned, purchase_qty/unit) to render the action buttons and
  // seed a new lot. Real catalog rows overwrite the synthesized stubs when
  // present.
  const ingById = useMemo(() => {
    const m = new Map<number, IngredientRow>()
    for (const s of shortages) {
      if (m.has(s.ingredient_id)) continue
      m.set(s.ingredient_id, {
        id: s.ingredient_id,
        slug: '',
        owned: s.ingredient_owned,
        name: s.name,
        purchase_qty: s.purchase_qty,
        purchase_unit: s.purchase_unit,
      })
    }
    for (const i of ingredients) m.set(i.id, i)
    return m
  }, [ingredients, shortages])

  // Group lots by a recipe/ingredient-aware key so recipe lots (no ingredient_id)
  // get their own group instead of collapsing under ingredient 0.
  const lotGroupKey = (p: PantryItem) => p.recipe_id ? `r${p.recipe_id}` : `i${p.ingredient_id}`

  const itemsByIng = useMemo(() => {
    const m = new Map<string, PantryItem[]>()
    for (const p of pantryItems) {
      const k = lotGroupKey(p)
      const arr = m.get(k) ?? []
      arr.push(p)
      m.set(k, arr)
    }
    for (const arr of m.values()) {
      arr.sort((a, b) => {
        const ax = a.expires_at ?? '9999'
        const bx = b.expires_at ?? '9999'
        return ax.localeCompare(bx)
      })
    }
    return m
  }, [pantryItems])

  // Build the on-hand list directly from pantry items so it doesn't depend on
  // the (paginated) ingredients catalog being loaded.
  const onHandGroups = useMemo(() => {
    const m = new Map<string, { key: string; name: string; isRecipe: boolean }>()
    for (const p of pantryItems) {
      const k = lotGroupKey(p)
      if (!m.has(k)) {
        m.set(k, { key: k, name: p.ingredient_name, isRecipe: !!p.recipe_id })
      }
    }
    return Array.from(m.values()).sort((a, b) => a.name.localeCompare(b.name))
  }, [pantryItems])

  const filteredAllIngredients = useMemo(() =>
    fterms.length ? ingredients.filter(i => matchesTerms(i.name, fterms)) : ingredients
  , [ingredients, fterms])

  const visibleShoppingItems = useMemo(() =>
    fterms.length ? shoppingItems.filter(s => matchesTerms(s.ingredient_name, fterms)) : shoppingItems
  , [shoppingItems, fterms])

  const visibleShortages = useMemo(() =>
    fterms.length ? shortages.filter(s => matchesTerms(s.name, fterms)) : shortages
  , [shortages, fterms])

  const visibleOnHandGroups = useMemo(() =>
    fterms.length ? onHandGroups.filter(g => matchesTerms(g.name, fterms)) : onHandGroups
  , [onHandGroups, fterms])

  const visiblePantryItems = useMemo(() =>
    fterms.length ? pantryItems.filter(p => matchesTerms(p.ingredient_name, fterms)) : pantryItems
  , [pantryItems, fterms])

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 22, maxWidth: 760 }}>
      <Header
        plannedCount={shortages.length}
        onhandCount={onHandGroups.length}
      />

      {error && (
        <div style={errorStyle}>{error}</div>
      )}

      {/* Ingredient filter */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        border: `1px solid ${theme.lineStrong}`, borderRadius: theme.radius,
        padding: '8px 12px', background: theme.surface,
      }}>
        <Ic.search width="16" height="16" style={{ color: theme.muted, flexShrink: 0 }}/>
        <input
          value={filterQuery}
          onChange={e => setFilterQuery(e.target.value)}
          placeholder="Filter by ingredient…"
          style={{ all: 'unset', flex: 1, fontFamily: theme.sans, fontSize: 14, color: theme.ink }}
        />
        {filterQuery && (
          <button
            onClick={() => setFilterQuery('')}
            style={{ all: 'unset', cursor: 'pointer', color: theme.muted, lineHeight: 1, fontSize: 16 }}
          >×</button>
        )}
      </div>

      <SubTabs
        value={subTab} onChange={setSubTab}
        shoppingCount={shoppingItems.length + shortages.length}
        viewMode={viewMode} onViewMode={setAndPersistViewMode}
      />

      {/* My shopping list */}
      {subTab === 'shopping' && (
      <Section
        title="My shopping list"
        subtitle={visibleShoppingItems.length === 0
          ? fq ? `No shopping list items matching "${filterQuery}".` : "Nothing on your list. Add items below or promote a suggestion."
          : `${visibleShoppingItems.length} item${visibleShoppingItems.length === 1 ? '' : 's'}.`}
        action={
          <button style={btnPrimary} onClick={() => setAddingShop(true)}>
            <Ic.plus width="14" height="14"/> Add item
          </button>
        }
      >
        {visibleShoppingItems.length > 0 && (
          <div style={cardListStyle}>
            {visibleShoppingItems.map(s => {
              const ing = ingById.get(s.ingredient_id) ?? {
                id: s.ingredient_id, slug: '', owned: s.ingredient_owned, name: s.ingredient_name,
              } as IngredientRow
              return (
                <div key={s.id} style={rowStyle}>
                  <div>
                    <div style={{ fontFamily: theme.sans, fontSize: 14, color: theme.ink }}>
                      <Highlight text={s.ingredient_name} terms={fterms}/>
                    </div>
                    <div style={{ fontFamily: theme.sans, fontSize: 12, color: theme.muted, marginTop: 2 }}>
                      {s.quantity} {s.unit}
                      {s.notes ? <> · {s.notes}</> : null}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button
                      style={btnSecondary}
                      onClick={() => openAddToPantry(ing, {
                        ingredient_id: s.ingredient_id, name: s.ingredient_name,
                        ingredient_owned: s.ingredient_owned, category: '',
                        required_canonical: 0, on_hand_canonical: 0, shortfall_canonical: 0,
                        purchase_qty: s.quantity, purchase_unit: s.unit, suggested_packs: 1,
                      })}
                    >Bought</button>
                    <button style={btnSecondary} onClick={() => setEditingShop(s)}>Edit</button>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </Section>
      )}

      {/* Suggestions */}
      {subTab === 'shopping' && (
      <Section title="Suggestions"
        subtitle={visibleShortages.length === 0
          ? fq ? `No suggestions matching "${filterQuery}".` : "Nothing flagged — your pantry covers the next two weeks of planned meals."
          : `${visibleShortages.length} ingredient${visibleShortages.length === 1 ? '' : 's'} running short.`}>
        {visibleShortages.length > 0 && (
          <div style={cardListStyle}>
            {visibleShortages.map(s => {
              const ing = ingById.get(s.ingredient_id)
              return (
                <div key={`${s.ingredient_id}-${s.category}`} style={rowStyle}>
                  <div>
                    <div style={{ fontFamily: theme.sans, fontSize: 14, color: theme.ink }}>
                      <Highlight text={s.name} terms={fterms}/>
                    </div>
                    <div style={{ fontFamily: theme.sans, fontSize: 12, color: theme.muted, marginTop: 2 }}>
                      need {fmtCanon(s.shortfall_canonical, s.category, unitPrefs)} more
                      {s.suggested_packs && s.purchase_qty && s.purchase_unit
                        ? <> — buy {s.suggested_packs} × {s.purchase_qty} {s.purchase_unit}</>
                        : null}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button style={btnPrimary} onClick={() => promoteSuggestion(s)}>Add</button>
                    {ing && (
                      <button
                        style={btnSecondary}
                        onClick={() => openAddToPantry(ing, s)}
                      >Bought</button>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </Section>
      )}

      {/* On hand — table view */}
      {subTab === 'stock' && viewMode === 'table' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button style={btnPrimary} onClick={() => setNewLotSeed({ ingredient: null as any })}>
              <Ic.plus width="14" height="14"/> Add lot
            </button>
          </div>
          <PantryTableView items={visiblePantryItems} />
        </>
      )}

      {/* On hand — list view */}
      {subTab === 'stock' && viewMode === 'list' && (
      <Section
        title="On hand"
        subtitle={visibleOnHandGroups.length === 0
          ? fq ? `No pantry lots matching "${filterQuery}".` : "Nothing tracked yet. Add a pantry lot to start."
          : undefined}
        action={
          <button style={btnPrimary} onClick={() => setNewLotSeed({ ingredient: null as any })}>
            <Ic.plus width="14" height="14"/> Add lot
          </button>
        }
      >
        {visibleOnHandGroups.length > 0 && (
          <div style={cardListStyle}>
            {visibleOnHandGroups.map(ing => {
              const lots = itemsByIng.get(ing.key) ?? []
              return (
                <div key={ing.key} style={{ ...rowStyle, alignItems: 'flex-start', flexDirection: 'column', gap: 6 }}>
                  <div style={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ fontFamily: theme.sans, fontSize: 14, color: theme.ink, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Highlight text={ing.name} terms={fterms}/>
                      {ing.isRecipe && (
                        <span style={{
                          fontSize: 9, letterSpacing: 1, textTransform: 'uppercase',
                          color: theme.accent, fontWeight: 700,
                          border: `1px solid ${theme.accent}`, borderRadius: 4, padding: '1px 4px',
                        }}>recipe</span>
                      )}
                    </div>
                    <div style={{ fontFamily: theme.sans, fontSize: 12, color: theme.muted }}>
                      {lots.length} lot{lots.length === 1 ? '' : 's'}
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, width: '100%' }}>
                    {lots.map(l => (
                      <button
                        key={l.id}
                        onClick={() => setEditingLot(l)}
                        style={lotStyle}
                      >
                        <span>{l.quantity} {l.unit || ''}</span>
                        {l.expires_at && (
                          <span style={expiryBadge(l.expires_at)}>
                            exp {l.expires_at}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </Section>
      )}

      {/* All known ingredients (catalog + cookbook) — list view only */}
      {subTab === 'stock' && viewMode === 'list' && (
      <Section
        title="All ingredients"
        subtitle="Catalog + your library. Click to set defaults and typical purchase amount."
        action={
          <button style={btnSecondary} onClick={() => setShowAllKnown(v => !v)}>
            {showAllKnown ? 'Hide' : 'Show'}
          </button>
        }
      >
        {showAllKnown && (
          <>
            {loading ? (
              <div style={{ fontFamily: theme.sans, fontSize: 13, color: theme.muted }}>Loading…</div>
            ) : (
              <div style={cardListStyle}>
                {filteredAllIngredients.map(ing => (
                  <button key={ing.id} onClick={() => openEdit(ing)} style={{
                    all: 'unset', cursor: 'pointer', width: '100%', boxSizing: 'border-box',
                    display: 'grid',
                    gridTemplateColumns: mobile ? '1fr auto' : '1fr 100px 100px',
                    gap: 12, alignItems: 'center',
                    padding: '10px 14px', borderTop: `1px solid ${theme.line}`,
                  }}>
                    <div style={{ fontFamily: theme.sans, fontSize: 14, color: theme.ink }}><Highlight text={ing.name} terms={fterms}/></div>
                    {!mobile && (
                      <div style={{ fontFamily: theme.sans, fontSize: 12, color: theme.inkSoft }}>
                        {ing.purchase_qty && ing.purchase_unit
                          ? `${ing.purchase_qty} ${ing.purchase_unit}`
                          : '—'}
                      </div>
                    )}
                    <div style={{
                      fontFamily: theme.sans, fontSize: 10, letterSpacing: 1, fontWeight: 600,
                      textTransform: 'uppercase', color: theme.muted, textAlign: 'right',
                    }}>{ing.owned ? 'yours' : ''}</div>
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </Section>
      )}

      <IngredientEditModal
        key={ingModalKey}
        open={!!editingIng}
        ingredient={editingIng}
        onCancel={() => setEditingIng(null)}
        onSaved={() => setEditingIng(null)}
      />

      <PantryItemEditModal
        key={lotModalKey}
        open={editingLot !== null || newLotSeed !== null}
        item={editingLot}
        seedIngredient={newLotSeed?.ingredient ?? null}
        seedQuantity={newLotSeed?.quantity}
        seedUnit={newLotSeed?.unit}
        seedQuery={newLotSeed && !newLotSeed.ingredient ? filterQuery : undefined}
        ingredients={ingredients}
        onClose={() => { setEditingLot(null); setNewLotSeed(null) }}
      />

      <ShoppingItemModal
        key={shopModalKey}
        open={editingShop !== null || addingShop}
        item={editingShop}
        onClose={() => { setEditingShop(null); setAddingShop(false) }}
      />

      {toast && (
        <div style={toastStyle} onClick={() => setToast(null)}>
          {toast}
        </div>
      )}
    </div>
  )
}

function canonicalUnit(category: string): string {
  if (category === 'mass') return 'g'
  if (category === 'volume') return 'ml'
  return 'ea'
}

function Header({ plannedCount, onhandCount }: { plannedCount: number; onhandCount: number }) {
  return (
    <div>
      <div style={{
        fontFamily: theme.sans, fontSize: 10, letterSpacing: 1.4, fontWeight: 600,
        textTransform: 'uppercase', color: theme.muted,
      }}>Pantry</div>
      <h1 style={{
        fontFamily: theme.serif, fontSize: 28, fontWeight: theme.headWeight,
        color: theme.ink, margin: '2px 0 0', lineHeight: 1.1,
      }}>What's on hand</h1>
      <div style={{
        fontFamily: theme.sans, fontSize: 13, color: theme.inkSoft, marginTop: 6,
      }}>
        Tracking {onhandCount} ingredient{onhandCount === 1 ? '' : 's'} on hand.
        {plannedCount > 0 ? ` ${plannedCount} flagged for shopping.` : ' Nothing flagged for shopping.'}
      </div>
    </div>
  )
}

function SubTabs({ value, onChange, shoppingCount, viewMode, onViewMode }: {
  value: 'stock' | 'shopping'
  onChange: (v: 'stock' | 'shopping') => void
  shoppingCount: number
  viewMode: 'list' | 'table'
  onViewMode: (m: 'list' | 'table') => void
}) {
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!menuOpen) return
    const handler = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [menuOpen])

  const tab = (v: 'stock' | 'shopping'): React.CSSProperties => ({
    all: 'unset', cursor: 'pointer',
    padding: '8px 14px',
    fontFamily: theme.sans, fontSize: 12, fontWeight: 600, letterSpacing: 0.4,
    textTransform: 'uppercase',
    color: value === v ? theme.ink : theme.muted,
    borderBottom: value === v ? `2px solid ${theme.ink}` : `2px solid transparent`,
    marginBottom: -1,
  })

  return (
    <div style={{ display: 'flex', gap: 4, borderBottom: `1px solid ${theme.line}`, alignItems: 'center' }}>
      <button style={tab('stock')} onClick={() => onChange('stock')}>Stock</button>
      <button style={tab('shopping')} onClick={() => onChange('shopping')}>
        Shopping{shoppingCount > 0 ? ` (${shoppingCount})` : ''}
      </button>
      <div style={{ flex: 1 }} />
      {value === 'stock' && (
        <div ref={menuRef} style={{ position: 'relative', marginBottom: -1 }}>
          <button
            onClick={() => setMenuOpen(v => !v)}
            title="View options"
            style={{
              all: 'unset', cursor: 'pointer',
              padding: '4px 8px', borderRadius: theme.radius,
              fontFamily: theme.sans, fontSize: 16, color: menuOpen ? theme.ink : theme.muted,
              lineHeight: 1,
            }}
          >⋮</button>
          {menuOpen && (
            <div style={{
              position: 'absolute', right: 0, top: 'calc(100% + 4px)',
              background: theme.surface, border: theme.cardBorder,
              borderRadius: theme.radius, zIndex: 50,
              boxShadow: '0 4px 16px rgba(20,14,8,0.14)', minWidth: 148, overflow: 'hidden',
            }}>
              {(['list', 'table'] as const).map(m => (
                <button
                  key={m}
                  onClick={() => { onViewMode(m); setMenuOpen(false) }}
                  style={{
                    all: 'unset', cursor: 'pointer', display: 'flex',
                    alignItems: 'center', justifyContent: 'space-between',
                    width: '100%', boxSizing: 'border-box',
                    padding: '10px 14px',
                    fontFamily: theme.sans, fontSize: 13, color: theme.ink,
                    background: viewMode === m ? `${theme.accent}18` : 'transparent',
                  }}
                >
                  {m === 'list' ? 'List view' : 'Table view'}
                  {viewMode === m && (
                    <span style={{ color: theme.accent, fontSize: 12, fontWeight: 700 }}>✓</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function Section({ title, subtitle, action, children }: {
  title: string; subtitle?: string; action?: React.ReactNode; children?: React.ReactNode
}) {
  return (
    <section>
      <div style={{
        display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
        marginBottom: 6,
      }}>
        <div>
          <h2 style={{
            fontFamily: theme.serif, fontSize: 18, fontWeight: theme.headWeight,
            color: theme.ink, margin: 0,
          }}>{title}</h2>
          {subtitle && (
            <div style={{ fontFamily: theme.sans, fontSize: 12, color: theme.muted, marginTop: 2 }}>
              {subtitle}
            </div>
          )}
        </div>
        {action}
      </div>
      {children}
    </section>
  )
}

function expiryBadge(expiresAt: string): React.CSSProperties {
  const days = Math.floor((new Date(expiresAt).getTime() - Date.now()) / 86400000)
  const urgent = days <= 3
  return {
    fontFamily: theme.sans, fontSize: 10, letterSpacing: 0.6, fontWeight: 600,
    textTransform: 'uppercase',
    color: urgent ? 'oklch(0.42 0.16 28)' : theme.muted,
    border: `1px solid ${urgent ? 'oklch(0.42 0.16 28 / 0.45)' : theme.line}`,
    borderRadius: 999, padding: '1px 8px',
  }
}

const cardListStyle: React.CSSProperties = {
  background: theme.surface, border: theme.cardBorder, borderRadius: theme.radiusLg,
  overflow: 'hidden',
}
const rowStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  gap: 12, padding: '10px 14px',
  borderTop: `1px solid ${theme.line}`, boxSizing: 'border-box', width: '100%',
}
const lotStyle: React.CSSProperties = {
  all: 'unset', cursor: 'pointer',
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  gap: 10, padding: '6px 10px', borderRadius: theme.radius,
  background: theme.bg, border: `1px solid ${theme.line}`,
  fontFamily: theme.sans, fontSize: 13, color: theme.ink, width: '100%',
  boxSizing: 'border-box',
}
const btnSecondary: React.CSSProperties = {
  all: 'unset', cursor: 'pointer',
  padding: '6px 10px', borderRadius: theme.radius,
  fontFamily: theme.sans, fontSize: 11, fontWeight: 600, letterSpacing: 0.4,
  textTransform: 'uppercase', color: theme.ink,
  border: `1px solid ${theme.lineStrong}`,
}
const btnPrimary: React.CSSProperties = {
  cursor: 'pointer',
  padding: '6px 12px', borderRadius: theme.radius,
  fontFamily: theme.sans, fontSize: 11, fontWeight: 700, letterSpacing: 0.4,
  textTransform: 'uppercase', color: theme.surface, background: theme.ink,
  border: 'none', display: 'inline-flex', alignItems: 'center', gap: 6,
}
const toastStyle: React.CSSProperties = {
  position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
  padding: '10px 16px', borderRadius: theme.radius,
  background: theme.ink, color: theme.surface,
  fontFamily: theme.sans, fontSize: 13,
  boxShadow: '0 6px 20px rgba(20,14,8,0.30)',
  zIndex: 100, maxWidth: '90vw', cursor: 'pointer',
}
const errorStyle: React.CSSProperties = {
  padding: '8px 12px', borderRadius: theme.radius,
  background: 'rgba(184,83,57,0.08)', color: theme.accentInk,
  fontFamily: theme.sans, fontSize: 12,
}
